import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ResultsContainer } from '@components/organisms/ResultsContainer';
import { FilterSidebarServices } from '@components/molecules/filters/FilterSidebarServices';
import { ServiceCardProps } from '@components/molecules/ServiceCard';
import { StickyHeader } from '@components/molecules/StickyHeader/StickyHeader';
import { MarketplaceAdvertising } from '@components/molecules/MarketplaceAdvertising';
import { Footer } from '@components/molecules/Footer';
import { Space } from '@components/atoms/Space';
import { useFilters } from '@hooks/useFilters';
import { useInfiniteScroll } from '@hooks/useInfiniteScroll';
import { useData } from '@hooks/useData';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { updateUrlSearchParams } from '@utils/search';
import { ResultsType } from '@types';

export interface ServicesSearchResultsProps {
    searchQuery: string;
}

export const ServicesSearchResults: FC<ServicesSearchResultsProps> = ({ searchQuery }) => {
    const { navigate } = useCustomNavigate();
    const { serviceStores, isLoadingServiceStores } = useData();
    const { results } = useFilters();
    const { pageToRequest, checkPageToRequest, observerRef } = useInfiniteScroll();
    const { t } = useTranslation();

    const translations = {
        orderNameAsc: t('sort-options.name_asc'),
        orderCreatedAtDesc: t('sort-options.created_at_desc'),
    };

    const handleOrderChange = (value: string) => {
        const updatedUrl = updateUrlSearchParams({ order: [value] });
        navigate(updatedUrl);
    };

    return (
        <>
            <StickyHeader />

            <ResultsContainer
                type={ResultsType.services}
                results={results as ServiceCardProps[]}
                scrollObserverRef={observerRef}
                filters={
                    <FilterSidebarServices
                        isSearchResultsPage={false}
                        pageToRequest={pageToRequest}
                        checkPageToRequest={checkPageToRequest}
                    />
                }
                total={serviceStores?.pagination?.total || 0}
                order={{
                    options: [
                        { value: 'name_asc', text: translations.orderNameAsc },
                        { value: 'created_at_desc', text: translations.orderCreatedAtDesc },
                    ],
                    onChange: handleOrderChange,
                }}
                searchQuery={searchQuery}
                loading={isLoadingServiceStores}
            />

            <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />

            <MarketplaceAdvertising />

            <Footer mode="normal" />
        </>
    );
};
